import React, { useState, useEffect, useRef } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import MapboxPicker from './MapboxPicker';

// Custom Phangan Island Outline Icon - Enhanced and Detailed
const PhanganIslandIcon = ({ className = "", size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    {/* Background gradient circle */}
    <defs>
      <linearGradient id="islandGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 0.1 }} />
        <stop offset="100%" style={{ stopColor: '#1d4ed8', stopOpacity: 0.2 }} />
      </linearGradient>
      <filter id="islandShadow">
        <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.2"/>
      </filter>
    </defs>
    
    {/* Outer glow circle */}
    <circle cx="16" cy="16" r="15" fill="url(#islandGradient)" />
    
    {/* Main island outline - more organic shape */}
    <path
      d="M16 4C11 4 7.5 6.5 6 9.5C4.5 12 4.5 14.5 5 17C5.5 19.5 7 21.5 9 23.5C11 25.5 13.5 26.5 16 26.5C18.5 26.5 21 25.5 23 23.5C25 21.5 26.5 19.5 27 17C27.5 14.5 27.5 12 26 9.5C24.5 6.5 21 4 16 4Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      filter="url(#islandShadow)"
    />
    
    {/* Inner bay/coast details */}
    <path
      d="M10 11C10.5 10 11.5 9.5 13 10C14.5 10.5 15 11.5 15 13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
    />
    
    <path
      d="M18 15C18.5 14.5 19.5 14 21 14.5C22.5 15 23 16 23 17.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
    />
    
    {/* Mountain peaks */}
    <path
      d="M13 16L15 13L17 16"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    
    {/* Beach/landmark dots */}
    <circle cx="11" cy="20" r="1.5" fill="currentColor" />
    <circle cx="20" cy="12" r="1.2" fill="currentColor" />
    <circle cx="16" cy="22" r="1" fill="currentColor" opacity="0.7" />
    
    {/* Wave patterns around island */}
    <path
      d="M8 8C7 8.5 6.5 9 6 10"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      opacity="0.4"
    />
    <path
      d="M24 8C25 8.5 25.5 9 26 10"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      opacity="0.4"
    />
  </svg>
);

// Custom Compass on Phangan Icon - Enhanced and Detailed
const CompassPhanganIcon = ({ className = "", size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    {/* Background and gradients */}
    <defs>
      <linearGradient id="compassGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: '#a855f7', stopOpacity: 0.1 }} />
        <stop offset="100%" style={{ stopColor: '#7c3aed', stopOpacity: 0.2 }} />
      </linearGradient>
      <radialGradient id="compassCenter">
        <stop offset="0%" style={{ stopColor: '#a855f7', stopOpacity: 0.3 }} />
        <stop offset="100%" style={{ stopColor: '#7c3aed', stopOpacity: 0 }} />
      </radialGradient>
      <filter id="compassShadow">
        <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.2"/>
      </filter>
    </defs>
    
    {/* Outer glow */}
    <circle cx="16" cy="16" r="15" fill="url(#compassGradient)" />
    
    {/* Phangan island outline (lighter) */}
    <path
      d="M16 5C11.5 5 8 7.5 6.5 10C5 12.5 5 15 5.5 17.5C6 19.5 7.5 21.5 9.5 23C11.5 24.5 13.5 25.5 16 25.5C18.5 25.5 20.5 24.5 22.5 23C24.5 21.5 26 19.5 26.5 17.5C27 15 27 12.5 25.5 10C24 7.5 20.5 5 16 5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      opacity="0.3"
    />
    
    {/* Compass outer ring */}
    <circle 
      cx="16" 
      cy="16" 
      r="9" 
      stroke="currentColor" 
      strokeWidth="2" 
      fill="url(#compassCenter)"
      filter="url(#compassShadow)"
    />
    
    {/* Inner compass ring */}
    <circle 
      cx="16" 
      cy="16" 
      r="6.5" 
      stroke="currentColor" 
      strokeWidth="1.5" 
      fill="none"
      opacity="0.6"
    />
    
    {/* Cardinal direction markers */}
    <line x1="16" y1="7" x2="16" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="16" y1="23" x2="16" y2="25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
    <line x1="7" y1="16" x2="9" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
    <line x1="23" y1="16" x2="25" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
    
    {/* North arrow (prominent) */}
    <path
      d="M16 10L17.5 14.5L16 13.5L14.5 14.5L16 10Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0.5"
      strokeLinejoin="round"
    />
    
    {/* South arrow (subtle) */}
    <path
      d="M16 22L17.5 17.5L16 18.5L14.5 17.5L16 22Z"
      fill="currentColor"
      opacity="0.4"
    />
    
    {/* East/West decorative markers */}
    <circle cx="22" cy="16" r="1" fill="currentColor" opacity="0.5" />
    <circle cx="10" cy="16" r="1" fill="currentColor" opacity="0.5" />
    
    {/* Diagonal direction hints */}
    <circle cx="20.5" cy="11.5" r="0.7" fill="currentColor" opacity="0.3" />
    <circle cx="11.5" cy="11.5" r="0.7" fill="currentColor" opacity="0.3" />
    <circle cx="20.5" cy="20.5" r="0.7" fill="currentColor" opacity="0.3" />
    <circle cx="11.5" cy="20.5" r="0.7" fill="currentColor" opacity="0.3" />
    
    {/* Center dot */}
    <circle cx="16" cy="16" r="1.5" fill="currentColor" />
  </svg>
);

const LocationInput = ({ label, value, onChange, placeholder, popularLocations = [], error }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [showGoogleMap, setShowGoogleMap] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (value && value?.length > 0) {
      const filtered = popularLocations?.filter((location) =>
        location?.toLowerCase()?.includes(value?.toLowerCase())
      );
      setFilteredLocations(filtered);
      if (document?.activeElement === inputRef?.current && filtered?.length > 0) {
        setShowSuggestions(true);
      }
    } else {
      setFilteredLocations(popularLocations);
      setShowSuggestions(false);
    }
  }, [value, popularLocations]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef?.current && !containerRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
      }
    };
    if (showSuggestions) {
      document?.addEventListener('mousedown', handleClickOutside);
      return () => document?.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  const handleLocationSelect = (location) => {
    onChange(location, null);
    setShowSuggestions(false);
    inputRef?.current?.blur();
  };

  const handleInputFocus = () => {
    if (popularLocations?.length > 0) {
      setFilteredLocations(popularLocations);
      setShowSuggestions(true);
    }
  };

  const handleInputChange = (e) => {
    onChange(e?.target?.value);
    if (e?.target?.value?.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleMapLocationSelect = (locationData) => {
    onChange(locationData?.address, locationData?.coordinates);
    setSelectedCoordinates(locationData?.coordinates);
    setShowGoogleMap(false);
    setShowSuggestions(false);
  };

  return (
    <>
      <div className="relative" ref={containerRef}>
        <div className="relative">
          <Input
            ref={inputRef}
            label={label}
            type="text"
            value={value}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            error={error}
            required
          />
        </div>

        {/* Select on Map button */}
        <button
          type="button"
          onClick={() => setShowGoogleMap(true)}
          className="mt-1.5 flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600 transition-colors font-medium px-1"
        >
          <Icon name="Map" size={13} />
          Select on Map
        </button>

        {showSuggestions && filteredLocations?.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-[500] max-h-64 overflow-y-auto custom-scrollbar">
            {filteredLocations?.map((location, index) => (
              <button
                key={index}
                onClick={() => handleLocationSelect(location)}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-secondary transition-colors text-left"
              >
                <Icon name="MapPin" size={18} className="text-accent flex-shrink-0" />
                <span className="text-foreground">{location}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <MapboxPicker
        isOpen={showGoogleMap}
        onClose={() => setShowGoogleMap(false)}
        onLocationSelect={handleMapLocationSelect}
        currentLocation={selectedCoordinates}
        title={`Select ${label}`}
      />
    </>
  );
};

export default LocationInput;